/**
 * The math-field submits LaTeX (e.g. \frac{1}{2}, x^{2}, \sqrt{9}, \text{a}) while answers
 * are authored and graded in plain ASCII (1/2, x^2, sqrt(9), a). Rewrite the LaTeX commands
 * the in-app keyboard layouts can emit into their ASCII spellings so comparisons can match.
 */
export function normalizeLatexAnswer(answer: string): string {
  const atom = (part: string): string => (/^-?[a-zA-Z0-9.,]*(\([^()]*\))?$/.test(part) ? part : `(${part})`)
  const unbrace = (part: string): string => part.replace(/^\{([^{}]*)\}$/, '$1')
  let previous = ''
  let normalized = answer
  // eslint-disable-next-line fp/no-loops
  while (normalized !== previous) {
    previous = normalized
    normalized = normalized
      .replace(/\\placeholder\{([^{}]*)\}/g, '$1')
      .replace(/\\text\{([^{}]*)\}/g, '$1')
      .replace(/\\mathrm\{([^{}]*)\}/g, '$1')
      // MathLive serializes a single-character argument without braces (4\sqrt7, \frac47),
      // so each argument is either {...} or one alphanumeric character.
      .replace(
        /\\frac(\{[^{}]*\}|[a-zA-Z0-9])(\{[^{}]*\}|[a-zA-Z0-9])/g,
        (_, num, den) => `${atom(unbrace(num))}/${atom(unbrace(den))}`,
      )
      .replace(/\\sqrt(\{[^{}]*\}|[a-zA-Z0-9])/g, (_, radicand) => `sqrt(${unbrace(radicand)})`)
      .replace(/\^\{([^{}]*)\}/g, '^$1')
  }
  return normalized
    .replace(/\\left|\\right/g, '')
    .replace(/[{}]/g, '')
    .replace(/\\cdot|\\times/g, '*')
    .replace(/\\div/g, '/')
    .replace(/\\leq(?![a-z])/g, '<=')
    .replace(/\\geq(?![a-z])/g, '>=')
    .replace(/\\le(?![a-z])/g, '<=')
    .replace(/\\ge(?![a-z])/g, '>=')
    .replace(/\\lt(?![a-z])/g, '<')
    .replace(/\\gt(?![a-z])/g, '>')
    .replace(/\\%/g, '%')
    .replace(/\\enspace|\\,|\\;|\\ |~/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
